import React, { useMemo } from 'react';
import { SearchBar, TitleBar, DropDown, PaginatedTable } from '../../common';
import { BUTTON, DISPATCH_COLUMN, ROUTES, TITLES } from '../../../constants';
import { dispatchDataCompiler, getLocalStorageItem } from '../../../utils';
import { Button, InputWithSearch } from '../../shared';
import {
  useDispatchUrlState,
  useDispatchFilters,
  useDispatchGroupBy,
  useDispatchPagination,
  useDispatchData,
  useDispatchSelection,
  useDispatchActions,
} from './hooks';
import { DispatchActionBar } from './components';

/**
 * DispatchContent - Dispatch content component for clothing (ESUN) vendor
 *
 * Features:
 * - GROUP_DISPATCH button
 * - PRINT_PACKING_LIST button (filters to dyeing partners only)
 * - CREATE_DISPATCH button (clothing only)
 * - Container No input
 * - No CREATE_BILL button
 * - Columns: Show Finished Product, Container No; Hide Lot Color/Size, Rib Color/Size
 * - Table class: table-esun
 * - Detail route: DISPATCH_DETAILS
 */
export const DispatchContent = () => {
  const userName = getLocalStorageItem('username');
  const isTexshann = userName === 'TEXSHANN EXPORTS';

  // Column configuration for clothing (ESUN) vendor
  const columnConfig = useMemo(
    () => ({
      show: ['Finished Product', 'Container No'],
      hide: ['Lot Color', 'Lot Size', 'Rib Color', 'Rib Size', ...(isTexshann ? ['Bill Status'] : [])],
    }),
    [isTexshann],
  );

  // Selection filters for clothing vendor
  // For TEXSHANN: Allow all draft records to be selectable
  const selectionFilters = useMemo(() => {
    const texshannCheck = getLocalStorageItem('username') === 'TEXSHANN EXPORTS';
    return {
      printMode: (row) =>
        row.partner_id?.vendor_type === 'dyeing' || row.vendor_id?.vendor_type === 'dyeing',
      billMode: (row) => {
        // Check raw data object which has lowercase status
        const rawStatus = row?.data?.dispatch_status || row?.dispatch_status;
        if (texshannCheck) {
          return rawStatus === 'draft' || rawStatus === 'Draft';
        }
        const vendorType = row?.data?.partner_id?.vendor_type || row?.partner_id?.vendor_type || 
                          row?.data?.vendor_id?.vendor_type || row?.vendor_id?.vendor_type;
        return (rawStatus === 'draft' || rawStatus === 'Draft') && vendorType === 'dyeing';
      },
      createBillMode: (row) => {
        const rawStatus = row?.data?.dispatch_status || row?.dispatch_status;
        const billId = row?.data?.dispatch_bill_id || row?.dispatch_bill_id;
        return (rawStatus === 'dispatched' || rawStatus === 'Dispatched' ||
                rawStatus === 'received' || rawStatus === 'Received') && !billId;
      },
    };
  }, []);

  // =====================================================================
  // COMPOSE HOOKS
  // =====================================================================

  // URL state management
  const urlState = useDispatchUrlState();

  // Filter state (search + sort)
  const filters = useDispatchFilters({
    initialState: urlState.initialState,
  });

  // GroupBy state
  const groupByState = useDispatchGroupBy({
    initialState: urlState.initialState,
    searchParams: urlState.searchParams,
  });

  // Pagination state
  const pagination = useDispatchPagination({
    initialPage: urlState.initialState.pageNumber,
  });

  // Data fetching (orchestrates effects)
  const data = useDispatchData({
    filters,
    groupByState,
    pagination,
    urlState,
  });

  // Selection state
  const selection = useDispatchSelection({
    dispatchData: data.dispatchData,
    paginationData: pagination.paginationData,
    selectableRecords: pagination.selectableRecords,
    printPackingSelectableRecords: pagination.printPackingSelectableRecords,
    createBillSelectableRecords: pagination.createBillSelectableRecords,
    columnConfig,
    selectionFilters,
  });

  // Actions
  const actions = useDispatchActions({
    selection,
    filters,
    groupByState,
    data,
    detailRoute: isTexshann ? ROUTES.YARN_DISPATCH_DETAIL : ROUTES.DISPATCH_DETAILS,
    postSubmitBehavior: 'clear', // After submit, clear the poList (clothing behavior)
    showContainerInput: true,
  });

  // =====================================================================
  // DESTRUCTURE FOR TEMPLATE
  // =====================================================================

  const {
    searchTerm,
    setSearchTerm,
    search,
    setSearch,
    dispatchStatusFilter,
    setDispatchStatusFilter,
    stateFilter,
    setStateFilter,
    billStatusFilter,
    setBillStatusFilter,
    dispatchStatusOptions,
    stateOptions,
    billStatusOptions,
    resetFilters,
  } = filters;

  const {
    groupArray,
    groupBy,
    setGroupBy,
    availableValuesGroupBy,
    setAvailableValuesGroupBy,
    selectedItem,
    setSelectedItem,
    removeGroupBy,
  } = groupByState;

  const { pageNumber, setPageNumber } = pagination;

  const { dispatchData, isLoading, getDispatchList } = data;

  const {
    billMode,
    printMode,
    createBillMode,
    filteredColumn,
    addPOToBill,
    checked,
    isBillMode,
    isPrintMode,
    isCreateBillMode,
  } = selection;

  const { detailRoute } = actions;

  const isAnyModeActive = billMode || printMode || createBillMode;

  // Handler for removeGroupBy that also fetches fresh data
  const handleRemoveGroupBy = () => {
    removeGroupBy({
      onRemove: () => {
        urlState.updateUrlParams({
          search: searchTerm,
          dispatchStatus:
            dispatchStatusFilter !== DISPATCH_COLUMN[16].name
              ? dispatchStatusFilter
              : '',
          state: stateFilter !== DISPATCH_COLUMN[17].name ? stateFilter : '',
          billStatus:
            billStatusFilter !== DISPATCH_COLUMN[15].name
              ? billStatusFilter
              : '',
          page: 1,
        });
        getDispatchList({
          page_number: 1,
          groupby_field: '',
          groupby_specifics: {},
        });
      },
    });
  };

  // Handler for reset filters that also fetches fresh data
  const handleResetFilters = () => {
    resetFilters();
    pagination.setPageNumber(1);
    getDispatchList({ page_number: 1 });
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.DISPATCH, route: ROUTES.DISPATCH }]} />
      <div className="d-flex align-items-center justify-content-start gap-3 w-100">
        <SearchBar
          search={search}
          setSearchTerm={setSearchTerm}
          searchTerm={searchTerm}
          setSearch={setSearch}
          onDefaultSearch={() => getDispatchList({ page_number: 1 })}
        />
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown
            dropdownMenu={dispatchStatusOptions}
            value={dispatchStatusFilter}
            setValue={setDispatchStatusFilter}
          />
          <DropDown
            dropdownMenu={stateOptions}
            value={stateFilter}
            setValue={setStateFilter}
          />
          {!isTexshann && (
            <DropDown
              dropdownMenu={billStatusOptions}
              value={billStatusFilter}
              setValue={setBillStatusFilter}
            />
          )}
          {(dispatchStatusFilter !== DISPATCH_COLUMN[16].name ||
            stateFilter !== DISPATCH_COLUMN[17].name ||
            (!isTexshann && billStatusFilter !== DISPATCH_COLUMN[15].name)) && (
            <Button
              clickHandler={handleResetFilters}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>
      <div className="d-block align-items-center justify-content-between gap-3 w-100">
        <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
          <DropDown dropdownMenu={groupArray} value={groupBy} setValue={setGroupBy} />
          {groupArray.includes(groupBy) && (
            <InputWithSearch
              setAvailableItem={setAvailableValuesGroupBy}
              availableItem={availableValuesGroupBy}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
            />
          )}
          {groupArray.includes(groupBy) && (
            <Button
              clickHandler={handleRemoveGroupBy}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.REMOVE}
            </Button>
          )}
        </div>

        {/* Action Bar - only shown when there's data */}
        {Array.isArray(dispatchData) && dispatchData?.length > 0 && (
          <DispatchActionBar
            selection={selection}
            actions={actions}
            selectableRecords={pagination.selectableRecords}
            features={{
              showGroupDispatch: true,
              showPrintPacking: !isTexshann,
              showCreateBill: false,
              showCreateDispatch: !isTexshann,
              showContainerInput: true,
            }}
          />
        )}
      </div>

      {/* Table */}
      {Array.isArray(dispatchData) ? (
        <PaginatedTable
          columns={filteredColumn}
          initialData={dispatchData}
          colFilter={true}
          to={isAnyModeActive ? '' : detailRoute}
          compiler={{ use: true, tool: dispatchDataCompiler }}
          func={{ addPOToBill, checked, isBillMode, isPrintMode, isCreateBillMode }}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={pagination.paginationData}
          isLoading={isLoading}
          className="table-1"
        />
      ) : (
        <div>
          <h4 className="text-center px-1 py-5">No Records Found</h4>
        </div>
      )}
    </>
  );
};
